import { useCallback, useState } from "react";
import {
  api,
  previews,
  type Preview,
  type PreviewPass,
  type IssuedPass,
  type PortalAudience,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import {
  Button,
  Failed,
  Field,
  Label,
  Select,
  SkeletonRows,
} from "../components/primitives";

/**
 * The live preview of a build, on the Release screen.
 *
 * **What "Create preview" does, and deliberately does not do.** It records a
 * hostname, the address the build is already hosted at, and who may open it.
 * It does not host anything, deploy anything, or build anything. Putting the
 * site somewhere is a manual job done outside this platform; this is the
 * access layer over the result, and the form says so, because a button called
 * "Create preview" that quietly did not create the site would be the worst
 * kind of honest.
 *
 * **Why it lives on Release.** A preview is "what can the client look at right
 * now", which is the question this screen already exists to answer, and it is
 * gated by `app.may_release` — the identical authority as publishing anything
 * else to a client. Putting it in a tab of its own would be a second place to
 * answer one question.
 *
 * **Why it is absent rather than empty on most engagements.** There is no
 * engagement type in this schema — nothing records "this one is a website
 * build" except the name a human typed. So the panel cannot know in advance
 * whether an engagement has something to preview, and guessing would put a
 * dead control on every advisory matter in the firm. Instead: nothing shows
 * until somebody who may release decides there is a build worth showing.
 *
 * **The client has no screen here and needs none.** Their door is the emailed
 * link. `origin_url` never reaches them either — by policy in 052, because an
 * origin they know is one they can visit directly for ever, with nothing we
 * could withdraw.
 */

const HOST_SUFFIX = "-preview.arikco.com";

export function PreviewPanel({
  engagementId,
  mayRelease,
}: {
  engagementId: string;
  mayRelease: boolean;
}) {
  const list = useResource<Preview[]>(
    () => previews.list(engagementId),
    [engagementId],
  );

  const [opening, setOpening] = useState(false);
  /** The link, held here rather than in the row that created it. */
  const [issued, setIssued] = useState<
    (IssuedPass & { fullName: string }) | null
  >(null);

  if (list.resource.state === "loading") {
    return (
      <section className="mt-12">
        <Label as="h2">Live preview</Label>
        <div className="mt-4">
          <SkeletonRows rows={2} />
        </div>
      </section>
    );
  }

  // A caller who may not release cannot read previews either: 052 gates the
  // read on may_release, because origin_url is on the row. The server answers
  // a forbidden READ with 404 rather than 403 — a 403 on a read is itself a
  // disclosure — so both arrive here as `absent` or `failed`, and the honest
  // render for both is nothing at all rather than an error box telling
  // somebody about a preview they are not allowed to know exists.
  if (list.resource.state === "failed" || list.resource.state === "absent") {
    return null;
  }

  const live = list.resource.data.find((p) => p.retiredAt === null) ?? null;
  const retiredCount = list.resource.data.filter((p) => p.retiredAt).length;

  // Nothing here, nothing to offer, and nobody with the standing to start.
  if (!live && !mayRelease && retiredCount === 0) return null;

  return (
    <section className="mt-12 border-t border-line pt-8">
      <Label as="h2">Live preview</Label>
      <p className="mt-2 max-w-[62ch] text-sm text-secondary">
        A build the client can look at before it is handed over, on its own
        hostname, openable only by a personal link we issue and can take back.
      </p>

      {issued && (
        <IssuedLink issued={issued} onDismiss={() => setIssued(null)} />
      )}

      {live ? (
        <LivePreview
          preview={live}
          engagementId={engagementId}
          mayRelease={mayRelease}
          onChanged={list.reload}
          onIssued={setIssued}
        />
      ) : (
        <div className="mt-5">
          {retiredCount > 0 && (
            <p className="text-sm text-secondary">
              {retiredCount === 1
                ? "One preview has been retired on this engagement."
                : `${retiredCount} previews have been retired on this engagement.`}
            </p>
          )}

          {mayRelease ? (
            opening ? (
              <CreateForm
                engagementId={engagementId}
                onCancel={() => setOpening(false)}
                onCreated={() => {
                  setOpening(false);
                  list.reload();
                }}
              />
            ) : (
              <div className="mt-3">
                <Button tone="primary" onClick={() => setOpening(true)}>
                  Create preview
                </Button>
                <p className="mt-2 max-w-[58ch] text-xs text-muted">
                  Records the address and who may open it. Hosting the build
                  itself is still a manual job — this does not deploy anything.
                </p>
              </div>
            )
          ) : (
            <p className="mt-3 text-sm text-secondary">
              No preview is open. Opening one is a Lead's or a Partner's.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** The form. Three fields, and one of them is half pre-written. */
function CreateForm({
  engagementId,
  onCancel,
  onCreated,
}: {
  engagementId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [originUrl, setOriginUrl] = useState("");

  const { invoke, pending, refusal } = useAction(async () => {
    await previews.create(engagementId, {
      // The suffix is fixed rather than typed. It is not a preference: a
      // two-label host has no TLS certificate under Cloudflare's free
      // Universal SSL, so `acme.preview.arikco.com` would fail the handshake
      // before any of our code ran. Composing it here means nobody can enter
      // a hostname that cannot work.
      host: `${subdomain.trim().toLowerCase()}${HOST_SUFFIX}`,
      originUrl: originUrl.trim(),
      label: label.trim(),
    });
    onCreated();
  });

  const clean = subdomain.trim().toLowerCase();
  const hostPreview = clean ? `${clean}${HOST_SUFFIX}` : null;
  const ready = clean && originUrl.trim() && label.trim();

  return (
    <form
      className="mt-4 max-w-[52ch]"
      onSubmit={(e) => {
        e.preventDefault();
        void invoke();
      }}
    >
      <div className="grid gap-4">
        <Field
          label="What it is"
          hint="How this appears to the firm. The client never sees it."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={200}
          autoComplete="off"
          data-autofocus="true"
        />

        <div>
          <Field
            label="Hostname"
            hint="One word, lowercase. The rest is fixed."
            value={subdomain}
            onChange={(e) =>
              setSubdomain(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))
            }
            maxLength={40}
            autoComplete="off"
          />
          <p className="mt-1.5 font-mono text-xs text-secondary">
            {hostPreview ?? `…${HOST_SUFFIX}`}
          </p>
        </div>

        <Field
          label="Where the build is hosted"
          hint="The address where the build is deployed, such as Pages or Vercel. It must use https. The client never sees this address."
          value={originUrl}
          onChange={(e) => setOriginUrl(e.target.value)}
          placeholder="https://"
          maxLength={500}
          autoComplete="off"
        />
      </div>

      {refusal && (
        <div className="mt-4">
          <Failed
            message={refusal.message}
            correlationId={refusal.correlationId}
          />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" tone="primary" loading={pending} disabled={!ready}>
          Create preview
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The open preview, and everyone who can get into it. */
function LivePreview({
  preview,
  engagementId,
  mayRelease,
  onChanged,
  onIssued,
}: {
  preview: Preview;
  engagementId: string;
  mayRelease: boolean;
  onChanged: () => void;
  onIssued: (p: IssuedPass & { fullName: string }) => void;
}) {
  const passes = useResource<PreviewPass[]>(
    () => previews.passes(preview.id),
    [preview.id],
  );

  const retire = useAction(async () => {
    await previews.retire(preview.id);
    onChanged();
  });

  const [confirming, setConfirming] = useState(false);

  return (
    <div className="mt-5">
      <div className="border border-line-strong p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-base text-ink">{preview.label}</p>
            <p className="mt-1 font-mono text-sm text-secondary">
              {preview.host}
            </p>
            <p className="mt-2 text-xs text-muted">
              serving {preview.originUrl}
            </p>
          </div>

          {mayRelease && (
            <div className="shrink-0">
              {confirming ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    tone="danger"
                    loading={retire.pending}
                    onClick={() => void retire.invoke()}
                  >
                    Retire it
                  </Button>
                  <Button onClick={() => setConfirming(false)}>Keep</Button>
                </div>
              ) : (
                <Button onClick={() => setConfirming(true)}>Retire</Button>
              )}
            </div>
          )}
        </div>

        {confirming && (
          <p className="mt-3 max-w-[58ch] text-sm text-secondary">
            Retiring closes every link into this preview immediately, including
            anyone reading it right now. The record of it stays.
          </p>
        )}

        {retire.refusal && (
          <div className="mt-4">
            <Failed
              message={retire.refusal.message}
              correlationId={retire.refusal.correlationId}
            />
          </div>
        )}
      </div>

      <div className="mt-6">
        <Label as="h3">Who can open it</Label>

        {passes.resource.state === "loading" && (
          <div className="mt-3">
            <SkeletonRows rows={2} />
          </div>
        )}

        {passes.resource.state === "failed" && (
          <div className="mt-3">
            <Failed
              message={passes.resource.message}
              correlationId={passes.resource.correlationId}
            />
          </div>
        )}

        {passes.resource.state === "ready" && (
          <>
            {passes.resource.data.length === 0 ? (
              <p className="mt-2 text-sm text-secondary">
                Nobody has access yet. Issue a personal link to let someone
                open the preview.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-line border-y border-line">
                {passes.resource.data.map((p) => (
                  <PassRow key={p.id} pass={p} onChanged={passes.reload} />
                ))}
              </ul>
            )}

            {mayRelease && (
              <IssueForm
                previewId={preview.id}
                engagementId={engagementId}
                already={passes.resource.data}
                onIssued={(issued, fullName) => {
                  onIssued({ ...issued, fullName });
                  passes.reload();
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PassRow({
  pass,
  onChanged,
}: {
  pass: PreviewPass;
  onChanged: () => void;
}) {
  const revoke = useAction(async () => {
    await previews.revoke(pass.id);
    onChanged();
  });

  const when = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-base text-ink">{pass.fullName}</p>
        <p className="mt-0.5 truncate text-xs text-secondary">{pass.email}</p>
        <p className="mt-1 text-xs text-muted">
          {pass.revokedAt
            ? `Revoked ${when(pass.revokedAt)}`
            : pass.live
              ? `Expires ${when(pass.expiresAt)}`
              : `Expired ${when(pass.expiresAt)}`}
          {" · "}
          {pass.uses === 0
            ? "not opened yet"
            : pass.lastUsedAt
              ? `last opened ${when(pass.lastUsedAt)}`
              : `opened ${pass.uses}×`}
        </p>
        {revoke.refusal && (
          <p className="mt-1 text-xs text-danger">{revoke.refusal.message}</p>
        )}
      </div>

      {pass.live && (
        <Button
          tone="quiet"
          loading={revoke.pending}
          onClick={() => void revoke.invoke()}
        >
          Revoke
        </Button>
      )}
    </li>
  );
}

/** Issue one named person a way in. */
function IssueForm({
  previewId,
  engagementId,
  already,
  onIssued,
}: {
  previewId: string;
  engagementId: string;
  already: PreviewPass[];
  onIssued: (issued: IssuedPass, fullName: string) => void;
}) {
  const audience = useResource<PortalAudience>(
    () => api.get<PortalAudience>(`/api/engagements/${engagementId}/portal-audience`),
    [engagementId],
  );
  const [userId, setUserId] = useState("");

  const { invoke, pending, refusal } = useAction(async () => {
    const people =
      audience.resource.state === "ready" ? audience.resource.data.people : [];
    const person = people.find((p) => p.userId === userId);
    const issued = await previews.issue(previewId, userId);
    onIssued(issued, person?.fullName ?? "them");
    setUserId("");
  });

  if (audience.resource.state !== "ready") return null;

  const people = audience.resource.data.people;
  if (people.length === 0) {
    return (
      <p className="mt-4 max-w-[58ch] text-sm text-secondary">
        This engagement has no client contacts yet, so there is nobody to issue
        a link to. Add them to the portal first.
      </p>
    );
  }

  const liveFor = new Set(already.filter((p) => p.live).map((p) => p.userId));

  return (
    <form
      className="mt-5 max-w-[46ch]"
      onSubmit={(e) => {
        e.preventDefault();
        void invoke();
      }}
    >
      <Select
        label="Issue a link to"
        hint="Their own link, emailed to them. Revoking it later closes their browser."
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      >
        <option value="">Choose a person…</option>
        {people.map((p) => (
          <option key={p.userId} value={p.userId}>
            {p.fullName}
            {liveFor.has(p.userId) ? " — already has one" : ""}
          </option>
        ))}
      </Select>

      {refusal && (
        <div className="mt-4">
          <Failed
            message={refusal.message}
            correlationId={refusal.correlationId}
          />
        </div>
      )}

      <div className="mt-4">
        <Button type="submit" tone="primary" loading={pending} disabled={!userId}>
          Issue a link
        </Button>
      </div>
    </form>
  );
}

/**
 * The link, shown once.
 *
 * Held by the panel rather than by the form that produced it, because the
 * form triggers a refetch and `useResource` unmounts its children while it
 * reloads. People.tsx learned that on 2026-10-27 by destroying a password a
 * fraction of a second after showing it.
 */
function IssuedLink({
  issued,
  onDismiss,
}: {
  issued: IssuedPass & { fullName: string };
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!issued.link) return;
    try {
      await navigator.clipboard.writeText(issued.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [issued.link]);

  return (
    <section className="mt-5 border border-line-strong p-5">
      <Label as="h3">Link for {issued.fullName}</Label>

      <p className="mt-2 max-w-[58ch] text-base text-ink">
        {issued.emailed
          ? "Emailed to them. It should arrive within a minute."
          : "Not emailed — send this to them yourself."}
      </p>

      <p className="mt-2 max-w-[58ch] text-sm text-secondary">
        Shown <strong className="text-ink">once</strong>. Only its hash is
        stored, so this cannot be shown again — issue another if it is lost.
      </p>

      {issued.link && (
        <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all bg-control p-4 font-mono text-sm text-ink">
          {issued.link}
        </pre>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {issued.link && (
          <Button tone="quiet" onClick={() => void copy()}>
            {copied ? "Copied" : "Copy the link"}
          </Button>
        )}
        <Button onClick={onDismiss}>Done</Button>
      </div>
    </section>
  );
}
